import { useState } from 'react';

import BlacklistIcon from '@/app/assets/svg/blacklist.svg?react';
import ActivityList from '@/app/components/ReferralPageComponents/ActivityList';
import GetPointsList from '@/app/components/ReferralPageComponents/GetPointsList';
import { usePointsLeaderboard } from '@/app/components/ReferralPageComponents/hooks/usePointsLeaderboard';
import PointsLeaderboardList from '@/app/components/ReferralPageComponents/PointsLeaderboardList';
import { formatPoints } from '@/app/shared/lib/formatters';
import { Button } from '@/app/shared/ui/Button';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const SUB_TABS = [
  { id: 'leaderboard' as const, label: 'Leaderboard' },
  { id: 'getPoints' as const, label: 'Get Points' },
  { id: 'activity' as const, label: 'Activity' },
];

type PointsSubTab = (typeof SUB_TABS)[number]['id'];

const MezzaninePointsSection = () => {
  const [activeSubTab, setActiveSubTab] = useState<PointsSubTab>('leaderboard');
  const { totalPoints, isLoading } = usePointsLeaderboard();
  const formattedPoints = totalPoints ? formatPoints(totalPoints) : '0';

  const isRestricted = false; // TODO: replace with real check black list user

  if (isRestricted) {
    return (
      <div className='flex min-h-75 flex-col items-center justify-center gap-5'>
        <div className='flex flex-col items-center gap-3 text-gray-50'>
          <BlacklistIcon />
          <p className='text-lg text-gray-50'>Restricted Jurisdiction</p>
        </div>

        <p className='max-w-md text-center leading-6 text-gray-400'>
          Mezzanine is not accessible to users in certain jurisdictions including locations subject to sanctions
          restrictions and other jurisdictions where our services are ineligible for use. For more information please
          see our Terms of Use.
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col'>
      <div className='flex w-full flex-col items-start justify-between gap-8 pt-3 pb-8 md:flex-row md:items-center md:pt-0 md:pb-10'>
        <div className='flex flex-1 flex-col gap-3'>
          <h2 className='text-base font-normal text-brand-1100'>Mezzanine Points</h2>
          <p className='max-w-95 text-base leading-6 text-gray-400'>
            Mezzanine Points tracks users early participation in the Mezzanine ecosystem.
          </p>
        </div>

        <div className='border-decoration-stroke flex w-full flex-1 flex-col gap-4.5 rounded-md border bg-transparent p-5 md:max-w-143.5'>
          <span className='text-base leading-none text-gray-400'>Total Points Awarded</span>
          <Skeleton.Line loading={isLoading} className='h-7 w-32'>
            <span className='text-2xl leading-none font-medium text-brand-1100'>{formattedPoints}</span>
          </Skeleton.Line>
        </div>
      </div>

      <div className='flex pb-5.5 md:pb-6'>
        <div className='-mx-5 flex overflow-x-auto px-5' style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {SUB_TABS.map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`shrink-0 rounded-lg px-2.5 py-1.75 leading-none transition-none ${
                activeSubTab === tab.id
                  ? 'bg-elevated-card-2 text-gray-50 [:root.light_&]:bg-(--vault-info-bg) [:root.light_&]:text-brand-1100'
                  : 'text-gray-400 hover:text-brand-1100'
              }`}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>

      <div className='border-decoration-stroke md:border-t'>
        {activeSubTab === 'leaderboard' && <PointsLeaderboardList />}
        {activeSubTab === 'getPoints' && <GetPointsList />}
        {activeSubTab === 'activity' && <ActivityList />}
      </div>
    </div>
  );
};

export default MezzaninePointsSection;
